import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { CameraCapture } from "@/components/smartfile/CameraCapture";
import { AudioRecorder } from "@/components/smartfile/AudioRecorder";
import { ArrowLeft, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface SubmissionCaptureProps {
  token: string;
  submissionType: 'photo' | 'audio' | 'file' | 'text';
  onSuccess: () => void;
  onBack: () => void;
}

export function SubmissionCapture({
  token,
  submissionType,
  onSuccess,
  onBack,
}: SubmissionCaptureProps) {
  const [loading, setLoading] = useState(false);
  const [textContent, setTextContent] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const handleSubmit = async (capturedFile?: File) => {
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('token', token);
      formData.append('submission_type', submissionType);

      if (submissionType === 'text') {
        if (!textContent.trim()) {
          toast.error('Por favor, escreva algum conteúdo');
          setLoading(false);
          return;
        }
        formData.append('text_content', textContent);
      } else {
        const fileToUpload = capturedFile || file;
        if (!fileToUpload) {
          toast.error('Por favor, selecione um arquivo');
          setLoading(false);
          return;
        }
        formData.append('file', fileToUpload);
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/submit-assisted-collection`,
        {
          method: 'POST',
          body: formData,
        }
      );

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      toast.success('Dados enviados com sucesso!');
      onSuccess();
    } catch (error: any) {
      toast.error(error.message || 'Erro ao enviar dados');
    } finally {
      setLoading(false);
    }
  };

  // Se for foto, renderizar componente fullscreen
  if (submissionType === 'photo') {
    return (
      <CameraCapture
        onCapture={(capturedFile) => handleSubmit(capturedFile)}
        onCancel={onBack}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={onBack} disabled={loading}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <CardTitle>
              {submissionType === 'audio' && 'Gravar Áudio'}
              {submissionType === 'file' && 'Enviar Arquivo'}
              {submissionType === 'text' && 'Escrever Texto'}
            </CardTitle>
            <CardDescription>
              {submissionType === 'audio' && 'Grave sua mensagem ou descrição'}
              {submissionType === 'file' && 'Selecione o arquivo que deseja enviar'}
              {submissionType === 'text' && 'Digite as informações que deseja compartilhar'}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">

        {submissionType === 'audio' && (
          <AudioRecorder
            onRecordingComplete={(audioFile) => handleSubmit(audioFile)}
            onCancel={onBack}
          />
        )}

        {submissionType === 'file' && (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
              <Input
                type="file"
                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center gap-2"
              >
                <Upload className="h-12 w-12 text-muted-foreground" />
                <p className="text-sm font-medium">
                  {file ? file.name : 'Clique para selecionar arquivo'}
                </p>
                <p className="text-xs text-muted-foreground">
                  PDF, imagens, Excel ou Word
                </p>
              </label>
            </div>
            <Button
              onClick={() => handleSubmit()}
              disabled={!file || loading}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                'Enviar Arquivo'
              )}
            </Button>
          </div>
        )}

        {submissionType === 'text' && (
          <div className="space-y-4">
            <Textarea
              placeholder="Digite aqui suas informações..."
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              rows={10}
              className="resize-none"
            />
            <Button
              onClick={() => handleSubmit()}
              disabled={!textContent.trim() || loading}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                'Enviar Texto'
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
